import { useCallback, useMemo, useState } from "react";

import { Button, CheckboxInput, ContentThumbnail, Drawer } from "@/components";
import { ListAdd } from "@/icons";
import { useAppProperties } from "@/hooks";
import { getContentThumbnailUrl } from "@/utils";

import {
  useDeleteFavourite,
  useDeleteWatchlistItem,
  useFavouriteDrawer,
  usePlaylistDrawer,
  usePutFavourite,
  usePutItemInPlaylists,
  usePutWatchlistItem,
  useWatchlistDrawer,
} from "./hooks";
import { SaveToPlaylistProps } from "./types.ts";
import { CreatePlaylistModal } from "./components/CreatePlaylistModal.tsx";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { DRAWER_PLAYLIST } from "@/const.ts";
import toast from "react-hot-toast";

interface OptionProps {
  name: string;
  thumbnail: string;
  included: boolean;
  onIncludedChange: (included: boolean) => void;
  dataTestId?: string;
}

const Option = ({
  name,
  thumbnail,
  included,
  onIncludedChange,
  dataTestId,
}: OptionProps) => {
  return (
    <div
      className="p1-semibold hover:bg-surface-secondary flex cursor-pointer items-center rounded-lg p-3"
      onClick={() => {
        onIncludedChange(!included);
      }}
    >
      <ContentThumbnail
        className=" mr-4 h-[68px] w-[122px] rounded-lg"
        thumbnail={thumbnail}
      />
      <div className="flex-grow overflow-hidden overflow-ellipsis text-nowrap">
        {name}
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <CheckboxInput
          checked={included}
          onCheckedChange={onIncludedChange}
          dataTestId={dataTestId}
          className="h-[20px] w-[20px] rounded-full"
        />
      </div>
    </div>
  );
};

export const SaveToPlaylistButton = ({
  ItemId,
  ItemType,
  SourceItemId,
}: SaveToPlaylistProps) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const {
    favouritesLabel,
    showWatchlist,
    showFavourites,
    watchlistLabel,
    showCustomPlaylists,
  } = useAppProperties();
  const putFavourite = usePutFavourite();
  const deleteFavourite = useDeleteFavourite();
  const putWatchlistItem = usePutWatchlistItem();
  const deleteWatchlistItem = useDeleteWatchlistItem();
  const putItemInPlaylists = usePutItemInPlaylists();
  const { openDrawer, setOpenDrawer } = useResponsiveness();
  const { translate } = useTranslate();

  const {
    firstWatchlistItem,
    watchlisted,
    setWatchlisted,
    currentlyWatchlisted,
    isLoading: isWatchlistLoading,
  } = useWatchlistDrawer({ ItemId, ItemType });

  const {
    firstFavourite,
    currentlyFavourited,
    favourited,
    setFavourited,
    isLoading: isFavouritesLoading,
  } = useFavouriteDrawer({
    ItemId,
    ItemType,
  });

  const {
    playlistIds,
    getFirstItemInPlaylist,
    isItemCurrentlyInPlaylist,
    isItemInPlaylist,
    setItemInPlaylist,
    getPlaylistName,
  } = usePlaylistDrawer({ ItemId, ItemType });

  const hideSaveButton = useMemo(() => {
    return !showWatchlist && !showFavourites;
  }, [showWatchlist, showFavourites]);

  const onSave = useCallback(() => {
    const added: string[] = []; // Names of playlists added to
    const removed: string[] = []; // Names of playlists removed from
    const promises = []; // Saves

    if (showFavourites && favourited !== currentlyFavourited) {
      if (favourited) {
        // Add new favourite
        added.push(favouritesLabel);
        promises.push(
          putFavourite.mutateAsync({
            ItemId: ItemId.toString(),
            ItemType,
            SourceItemId,
          }),
        );
      } else {
        removed.push(favouritesLabel);
        // Remove from favourites
        promises.push(
          deleteFavourite.mutateAsync({
            ItemId: ItemId.toString(),
            ItemType,
            SourceItemId,
          }),
        );
      }
    }
    if (showWatchlist && watchlisted !== currentlyWatchlisted) {
      if (watchlisted) {
        added.push(watchlistLabel);
        promises.push(
          putWatchlistItem.mutateAsync({
            ItemId: ItemId.toString(),
            ItemType,
            SourceItemId,
          }),
        );
      } else {
        removed.push(watchlistLabel);
        promises.push(
          deleteWatchlistItem.mutateAsync({
            ItemId: ItemId.toString(),
            ItemType,
            SourceItemId,
          }),
        );
      }
    }
    // For each playlist check if we need to add/remove it
    const AddToPlaylists: number[] = [];
    const RemoveFromPlaylists: number[] = [];
    if (showCustomPlaylists && playlistIds) {
      for (const _id of playlistIds) {
        if (isItemCurrentlyInPlaylist(_id) !== isItemInPlaylist(_id)) {
          if (isItemInPlaylist(_id)) {
            AddToPlaylists.push(_id);
            added.push(getPlaylistName(_id));
          } else {
            RemoveFromPlaylists.push(_id);
            removed.push(getPlaylistName(_id));
          }
        }
      }
      if (AddToPlaylists?.length > 0 || RemoveFromPlaylists?.length > 0) {
        promises.push(
          putItemInPlaylists.mutateAsync({
            ItemId,
            ItemType,
            AddToPlaylists,
            RemoveFromPlaylists,
          }),
        );
      }
    }

    setOpenDrawer("");
    // Wait for all the saves to resolve
    Promise.all(promises).then(() => {
      if (!(added.length == 0 && removed.length == 0)) {
        // Something changed, show a toast message
        if (added.length === 1 && removed.length === 0) {
          toast(`${translate("Added to")} ${added[0]}`);
        } else if (added.length === 0 && removed.length === 1) {
          toast(`${translate("Removed from")} ${removed[0]}`);
        } else if (added.length > 1) {
          toast(`${translate("Added to")} ${translate("playlists")}`);
        } else if (removed.length > 1) {
          toast(`${translate("Removed from")} ${translate("playlists")}`);
        }
      }
    });
  }, [
    showFavourites,
    favourited,
    currentlyFavourited,
    showWatchlist,
    watchlisted,
    currentlyWatchlisted,
    showCustomPlaylists,
    playlistIds,
    setOpenDrawer,
    favouritesLabel,
    putFavourite,
    ItemId,
    ItemType,
    SourceItemId,
    deleteFavourite,
    watchlistLabel,
    putWatchlistItem,
    deleteWatchlistItem,
    isItemCurrentlyInPlaylist,
    isItemInPlaylist,
    getPlaylistName,
    putItemInPlaylists,
    translate,
  ]);

  if (hideSaveButton || isWatchlistLoading || isFavouritesLoading) {
    return null;
  }
  const isInPlaylist = playlistIds?.some((_id) =>
    isItemCurrentlyInPlaylist(_id),
  );
  const highlighted =
    currentlyFavourited || currentlyWatchlisted || isInPlaylist;

  return (
    <>
      <Button
        data-testid="playlist-button"
        variant={highlighted ? "secondary_focussed" : "secondary"}
        onClick={() => setOpenDrawer(DRAWER_PLAYLIST)}
        size="s"
      >
        <ListAdd className="mr-1 h-5 w-5" />{" "}
        {highlighted ? translate("Saved") : translate("Save")}
      </Button>
      <Drawer
        title={translate("Save to playlist")}
        open={openDrawer === DRAWER_PLAYLIST}
        onOpenChange={(open) => setOpenDrawer(open ? DRAWER_PLAYLIST : "")}
        dataTestId="playlist-drawer"
        className="px-3"
        titleClassName="pb-2"
      >
        <div className="flex h-full flex-col gap-0">
          {showFavourites && (
            <Option
              name={favouritesLabel}
              included={favourited}
              onIncludedChange={(i) => setFavourited(i)}
              thumbnail={
                firstFavourite ? getContentThumbnailUrl(firstFavourite) : ""
              }
              dataTestId="favourites-checkbox"
            />
          )}
          {showWatchlist && (
            <Option
              name={watchlistLabel}
              thumbnail={
                firstWatchlistItem
                  ? getContentThumbnailUrl(firstWatchlistItem)
                  : ""
              }
              included={watchlisted}
              onIncludedChange={(i) => setWatchlisted(i)}
              dataTestId="watchlist-checkbox"
            />
          )}

          {(showFavourites || showWatchlist) && playlistIds && (
            <div className="bg-border m-3 h-[1px] w-full" />
          )}

          {showCustomPlaylists &&
            playlistIds?.map((_id) => {
              const firstItem = getFirstItemInPlaylist(_id);
              return (
                <Option
                  key={_id}
                  name={getPlaylistName(_id)}
                  thumbnail={firstItem ? getContentThumbnailUrl(firstItem) : ""}
                  included={isItemInPlaylist(_id)}
                  onIncludedChange={(i) => setItemInPlaylist(_id, i)}
                />
              );
            })}
          <div className="mb-8 flex flex-grow items-end ">
            <div className="flex w-full flex-col gap-3">
              <CreatePlaylistModal
                open={isModalOpen}
                setOpen={setModalOpen}
                onSuccess={(newId) => setItemInPlaylist(newId, true)}
              />
              <Button
                className="w-full"
                onClick={onSave}
                data-testid="playlist-save-button"
              >
                {translate("Confirm")}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};
